import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Chip,
  Paper,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Loading from '../../../../components/loading';
import ErrorMessage from '../../../../components/errorMessage';
import { usePatientAppointments } from '../hooks/usePatientAppointments';
import {
  formatDuration,
  formatText,
  getStatusColor,
  getStatusLabel,
  sortByDateDesc,
} from '../appointmentFields';

export default function AppointmentListWeb() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { appointments, isLoading, error: listError } = usePatientAppointments(patientId);

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const sortedAppointments = useMemo(() => sortByDateDesc(appointments), [appointments]);

  const openAppointment = (appointmentId) => {
    navigate(`/appointment/${appointmentId}`);
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Consultas
          </Typography>
        </Box>

        {sortedAppointments.length === 0 ? (
          <Typography align="center" sx={{ py: 4 }}>
            Nenhuma consulta encontrada para este paciente.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Hora</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Duração</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Profissional</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Situação</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Observação</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAppointments.map((appointment) => (
                  <TableRow key={appointment.id} hover>
                    <TableCell>{formatText(appointment.date)}</TableCell>
                    <TableCell>{formatText(appointment.time)}</TableCell>
                    <TableCell>{formatDuration(appointment)}</TableCell>
                    <TableCell>{formatText(appointment.dentistName)}</TableCell>
                    <TableCell>{formatText(appointment.appointmentType)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getStatusLabel(appointment)}
                        color={getStatusColor(appointment)}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Tooltip title={appointment.observation ?? ''}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatText(appointment.observation)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Abrir consulta">
                        <IconButton color="primary" onClick={() => openAppointment(appointment.id)}>
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </>
  );
}
